import { CalendarProvider } from '@rentnerkev/calendar'
import { InputProvider } from '@rentnerkev/inputs'
import { PickerProvider } from '@rentnerkev/picker'
import { SelectProvider } from '@rentnerkev/select'
import { ToastProvider } from '@rentnerkev/toasts'
import { TooltipProvider } from '@rentnerkev/tooltips'
import { TanStackDevtools } from '@tanstack/react-devtools'
import type { QueryClient } from '@tanstack/react-query'
import {
    HeadContent,
    Scripts,
    createRootRouteWithContext,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import * as React from 'react'

import { APP_DESCRIPTION, DEFAULT_APP_TITLE } from '@/config/app.config.ts'
import TransferOverlay from '@/shared/TransferOverlay'

import TanStackQueryDevtools from '../integrations/TanstackQuery/devtools'
import useTranslationStore, {
    translationReady,
} from '../language/useTranslationStore'

import '../styles.css'

interface MyRouterContext {
    queryClient: QueryClient
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
    beforeLoad: async () => {
        await translationReady
    },
    head: () => ({
        meta: [
            {
                charSet: 'utf-8',
            },
            {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1',
            },
            {
                title: DEFAULT_APP_TITLE,
            },
            {
                name: 'description',
                content: APP_DESCRIPTION,
            },
        ],
        links: [
            {
                rel: 'icon',
                href: '/favicon.ico',
            },
        ],
    }),
    shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
    const { language } = useTranslationStore()

    return (
        <html lang={language}>
            <head>
                <HeadContent />
            </head>
            <body
                suppressHydrationWarning
                className="[--rentner-accent-foreground:#fff] [--rentner-accent-hover:var(--color-primary-hover)] [--rentner-accent:var(--color-primary)] [--rentner-border:var(--color-border-dark)] [--rentner-control:var(--color-input-dark)] [--rentner-foreground:var(--color-white)] [--rentner-muted:var(--color-secondary-text)] [--rentner-radius:0.5rem] [--rentner-surface:var(--color-surface-dark)]"
            >
                <TooltipProvider>
                    <InputProvider
                        locale={language}
                        classNames={{
                            label: 'mb-2 block text-sm font-semibold text-white',
                        }}
                    >
                        <SelectProvider locale={language}>
                            <CalendarProvider locale={language}>
                                <PickerProvider locale={language}>
                                    <ToastProvider
                                        locale={language}
                                        position="bottom-right"
                                        className="w-100 max-w-[calc(100vw-2rem)]"
                                    >
                                        {children}
                                        <TransferOverlay />
                                    </ToastProvider>
                                </PickerProvider>
                            </CalendarProvider>
                        </SelectProvider>
                    </InputProvider>
                </TooltipProvider>
                <TanStackDevtools
                    config={{
                        position: 'bottom-right',
                    }}
                    plugins={[
                        {
                            name: 'Tanstack Router',
                            render: <TanStackRouterDevtoolsPanel />,
                        },
                        TanStackQueryDevtools,
                    ]}
                />
                <Scripts />
            </body>
        </html>
    )
}
